"use client";
import * as React from "react";
import { useNavigate } from 'react-router-dom';

function ModernFooter() {
  const navigate = useNavigate();

  const handleNavClick = (path) => {
    if (path.startsWith('#')) {
      // For anchor links, navigate to contact page first then scroll to section
      navigate('/contact');
      setTimeout(() => {
        // First scroll to top
        window.scrollTo(0, 0);
        // Then wait a bit more for page to load and scroll to section
        setTimeout(() => {
          const element = document.querySelector(path);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }, 100);
    } else {
      navigate(path);
      // Scroll to top after navigation
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  const footerLinks = {
    QuickLinks: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
      { name: "Product", path: "/product" },
    ],
    Services: [
      { name: "Consulting", path: "/consulting" },
      { name: "Outsourcing", path: "/outsourcing" },
    ],
    Company: [
      { name: "About", path: "/about" },
      { name: "Careers", path: "/careers" },
      { name: "Contact", path: "/contact" },
    ]
  };

  return (
    <footer className="relative" style={{ backgroundColor: '#0B1026' }}>
      {/* Top Curved Divider */}
      <div className="absolute top-0 left-0 right-0">
        <svg viewBox="0 0 1200 80" className="w-full h-auto">
          <path
            d="M0,40 C300,0 500,80 800,40 C1000,10 1100,60 1200,40 L1200,0 L0,0 Z"
            fill="#0B1026"
          />
        </svg>
      </div>

      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-20 left-1/4 w-64 h-64 rounded-full opacity-5 blur-3xl"
          style={{ background: 'radial-gradient(circle, #1719A8 0%, transparent 70%)' }}
        ></div>
        <div
          className="absolute bottom-20 right-1/4 w-48 h-48 rounded-full opacity-5 blur-2xl"
          style={{ background: 'radial-gradient(circle, #D96F2B 0%, transparent 70%)' }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-16">
          {/* Brand Section */}
          <div className="md:col-span-2 lg:col-span-1 space-y-6">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleNavClick('/')}
                className="focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50  transition-all duration-300 hover:scale-105"
                aria-label="Vasta - Go to Home"
              >
                <img
                  src="https://cdn.acsdev.in/Vasta/Vasta%20-%20Logo.jpg.jpeg"
                  alt="Vasta Logo"
                  className="h-10 w-40 lg:h-16 w-auto py-1 px-3 bg-white object-contain cursor-pointer"
                />
              </button>
            </div>

            <p className="text-white/70 leading-relaxed max-w-md">
              Transforming enterprises with AI-powered solutions that drive innovation,
              efficiency, and unprecedented business growth.
            </p>

            <div className="flex space-x-4">
              <a
                href="https://x.com/vastainfoglobal"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300"
                aria-label="Follow us on X"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/vastainfoglobal/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300"
                aria-label="Follow us on LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href="https://www.facebook.com/people/Vasta-Info-Global/100090115755576/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300"
                aria-label="Follow us on Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className="space-y-4">
              <h3 className="text-white font-bold text-lg">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => handleNavClick(link.path)}
                      className="text-white/70 hover:text-white transition-colors duration-300 text-sm text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/60 text-sm">
              © 2022 VASTA. All rights reserved.
            </div>
            <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-6">
              <div className="flex space-x-6 text-sm">
                <button
                  onClick={() => handleNavClick('#privacy')}
                  className="text-white/60 hover:text-white transition-colors duration-300"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => handleNavClick('#terms')}
                  className="text-white/60 hover:text-white transition-colors duration-300"
                >
                  Terms of Service
                </button>
                <button
                  onClick={() => handleNavClick('#cookies')}
                  className="text-white/60 hover:text-white transition-colors duration-300"
                >
                  Cookie Policy
                </button>
              </div>
              {/* E-verify Badge positioned near policies */}
              <div className="flex items-center">
                <img
                  src="https://cdn.acsdev.in/Vasta/verify.jpeg"
                  alt="E-Verify Employer"
                  className="h-8 w-auto object-contain bg-white/10 backdrop-blur-sm rounded px-2 py-1 border border-white/20"
                  title="E-Verify Participating Employer"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default ModernFooter;
